/**
 * Research Engine — the composer.
 *
 * Merges the composable knowledge sources for a request into one Research Brief
 * and computes its confidence, freshness, and provenance. Pure, deterministic,
 * and cacheable: the brief is a function of the request (and any explicit
 * overrides) alone.
 *
 * `overrides` is the deterministic hook for future Brand/Tenant intelligence —
 * a list of high-precedence fragments merged above every dimension. It is a
 * real, usable parameter today.
 */

import { RESEARCH_SCHEMA_ID, RESEARCH_VERSION, REQUEST_DIMENSIONS } from './researchSchema';
import { mergeFragments } from './mergeFragments';
import { getOrderedFragments } from './knowledge';

const round2 = (n) => Math.round(n * 100) / 100;
const clamp01 = (n) => Math.max(0, Math.min(1, n));

/** Overall confidence: source quality scaled by how much of the request we covered. */
const computeConfidence = (fragments, requestedDims, matchedCount) => {
  const confidences = fragments.map((f) => (typeof f.confidence === 'number' ? f.confidence : 0.4));
  const avg = confidences.reduce((a, b) => a + b, 0) / confidences.length;
  const coverage = requestedDims === 0 ? 1 : matchedCount / requestedDims;
  return round2(clamp01(avg * (0.55 + 0.45 * coverage)));
};

/** Freshness: the oldest `asOf` among contributing fragments (a brief is only as fresh as its stalest input). */
const computeFreshness = (fragments) => {
  const stamps = fragments.map((f) => f.asOf).filter((s) => typeof s === 'string');
  return stamps.length ? stamps.slice().sort()[0] : null;
};

/**
 * Compose a Research Brief.
 * @param {Object} request  Any subset of REQUEST_DIMENSIONS (industry, category, …).
 * @param {Object} [options]
 * @param {Object[]} [options.overrides]  High-precedence fragments (future brand/tenant).
 * @returns {Object} the Research Brief.
 */
export const composeResearchBrief = (request = {}, { overrides = [] } = {}) => {
  const { fragments, labels, matchedDims } = getOrderedFragments(request);
  const ordered = [...fragments, ...overrides];              // overrides are most specific
  const sourceLabels = [...labels, ...overrides.map((_, i) => `override:${i}`)];

  const merged = mergeFragments(ordered);
  const requestedDims = REQUEST_DIMENSIONS.filter((d) => request[d]).length;

  return {
    schema: RESEARCH_SCHEMA_ID,
    version: RESEARCH_VERSION,
    request: { ...request },
    ...merged,
    confidence: computeConfidence(ordered, requestedDims, matchedDims.length),
    freshness: computeFreshness(ordered),
    sources: sourceLabels,
  };
};
